import { useI18n } from '@i18n';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import Scrollbar from '@/components/Scrollbar';
import { mergeSx } from '@/utils/cssStyles';

type BaseProps = Omit<DialogProps, 'actions' | 'children' | 'content'> & {
  open: boolean;
  onClose: () => void;
  showClose?: boolean;
  disableCloseAction?: boolean;
  sx?: SxProps;
};

type DialogContentProps = {
  dialogtitle: ReactNode | string;
  content: ReactNode;
  actions: ReactNode;
  /**
   * If true, two dividers will be shown between title, content and actions
   * @default false
   */
  showDividers?: boolean;
  /**
   * Height of the scrollbar. If provided, the content will be scrollable and actions will be sticky
   */
  scrollBarContentHeight?: string;
  actionsSx?: SxProps;
  dialogContentSx?: SxProps;
};

type DefinedByProperty = BaseProps &
  DialogContentProps & {
    type: 'definedByProperty';
  };

type DefinedByChildren = BaseProps & {
  type: 'definedByChildren';
  children: ReactNode;
};

type CloseIconProps = {
  id?: string;
  onClose: VoidFunction;
  disabled?: boolean;
  sx?: SxProps;
};

export const CloseIcon = ({ onClose, disabled = false, sx, id }: CloseIconProps) => {
  const { t } = useI18n();
  return (
    <IconButton
      id={id}
      aria-label={t('General_Close')}
      sx={mergeSx({ position: 'absolute', top: 20, right: 12, zIndex: 5 }, sx)}
      onClick={onClose}
      disabled={disabled}
    >
      <Iconify icon={ICONS.CHARM_CROSS} width={20} height={20} />
    </IconButton>
  );
};

export const PreDialogBody = ({
  dialogtitle,
  content,
  actions,
  showDividers = false,
  scrollBarContentHeight,
  actionsSx,
  dialogContentSx,
}: DialogContentProps) => {
  const theme = useTheme();
  return (
    <>
      {typeof dialogtitle === 'string' ? (
        <DialogTitle
          style={{ overflow: 'hidden' }}
          sx={{ borderBottom: showDividers ? `1px solid ${theme.palette.grey[500_24]}` : undefined }}
        >
          <PTypography variant="h6">{dialogtitle}</PTypography>
        </DialogTitle>
      ) : (
        dialogtitle
      )}
      {scrollBarContentHeight ? (
        <Scrollbar
          sx={{
            // make actions sticky and content scrollable by fixing maxHeight
            maxHeight: scrollBarContentHeight,
          }}
        >
          <DialogContent sx={dialogContentSx}>{content}</DialogContent>
        </Scrollbar>
      ) : (
        <DialogContent sx={dialogContentSx}>{content}</DialogContent>
      )}
      <DialogActions
        sx={{
          borderTop: showDividers ? `1px solid ${theme.palette.grey[500_24]}` : undefined,
          overflow: 'hidden',
          ...actionsSx,
        }}
      >
        {actions}
      </DialogActions>
    </>
  );
};

type PreDialogTitleIconProps = {
  icon?: string | ReactNode;
};

type PreDialogTitleProps = PreDialogTitleIconProps & {
  title: string;
};

export const PreDialogTitle = ({ icon, title }: PreDialogTitleProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'start',
        p: 3,
        //Need some space between close icon so different right padding
        pr: 5,
      }}
    >
      {icon ? (
        typeof icon === 'string' ? (
          <Iconify width={24} height={24} icon={icon} sx={{ mr: 1, mt: 0.5 }} />
        ) : (
          icon
        )
      ) : null}

      <PTypography variant="h5">{title}</PTypography>
    </Box>
  );
};

/**
 *
 * @param props discriminator properties. You can use either of these to create a default Dialog with less boilerplate or create your own Dialog by providing all Elements as children.
 *
 *  * @returns A vanilla MUI Dialog with the appropriate inner components or an layer around your own content.
 */
function PreDialog(props: DefinedByChildren | DefinedByProperty) {
  if (props.type === 'definedByChildren') {
    const {
      onClose,
      open,
      showClose = true,
      disableCloseAction = false,
      children,
      type: _type,
      ...dialogProps
    } = props;
    return (
      <Dialog
        open={open}
        onClose={() => {
          if (disableCloseAction) return;
          onClose();
        }}
        {...dialogProps}
        disableEscapeKeyDown={disableCloseAction}
      >
        {showClose ? <CloseIcon onClose={onClose} disabled={disableCloseAction} /> : null}
        {children}
      </Dialog>
    );
  } else if (props.type === 'definedByProperty') {
    const {
      onClose,
      open,
      showClose = true,
      disableCloseAction = false,
      dialogtitle,
      content,
      actions,
      showDividers,
      scrollBarContentHeight,
      actionsSx,
      dialogContentSx,
      type: _type,
      ...dialogProps
    } = props;
    return (
      <Dialog
        open={open}
        onClose={() => {
          if (disableCloseAction) return;
          onClose();
        }}
        {...dialogProps}
        disableEscapeKeyDown={disableCloseAction}
      >
        {showClose ? <CloseIcon onClose={onClose} disabled={disableCloseAction} /> : null}
        <PreDialogBody
          dialogtitle={dialogtitle}
          content={content}
          actions={actions}
          showDividers={showDividers}
          scrollBarContentHeight={scrollBarContentHeight}
          actionsSx={actionsSx}
          dialogContentSx={dialogContentSx}
        />
      </Dialog>
    );
  }

  return null;
}

export default PreDialog;
